import { defineStore } from 'pinia';
import { readCookie } from '@/modules/cookies';
import { type NumericInputMethod, usePartnerConfigStore } from '@/stores/partnerConfig';

export interface PreferenceResponse {
  id?: number | string;
  name?: string;
  selected?: boolean;
  unknown?: boolean;
  value?: unknown;
  [key: string]: any;
}

export interface PreferencePanel {
  id?: number | string;
  name?: string;
  type?: string;
  input?: string;
  collections?: string;
  computation?: string;
  options?: Record<string, any>;
  responses?: PreferenceResponse[];
  [key: string]: any;
}

export interface PreferenceOrder {
  total: number;
  ind: number;
}

// the shape v4/init receives from api/v3/conf.json
interface Config {
  preferences: PreferencePanel[];
  hashes: {
    preferences: string;
  };
}

interface PreferenceState {
  preferences: PreferencePanel[] | null;
  hash: string | null;
  engagementKey: string | null;
  heartRateInputMethod: NumericInputMethod | null;
  respiratoryRateInputMethod: NumericInputMethod | null;
}

// helper functions

// Carries existing answers onto freshly fetched panels. Verbatim from store/v3/mutations.js.
// Mutates and returns newPreferences: assign it in one shot, or keys conf.json omits
// (`unknown`) end up non-reactive.
export const hydrateFromExisting = (
  oldPreferences: PreferencePanel[] = [],
  newPreferences: PreferencePanel[] = [],
): PreferencePanel[] => {
  oldPreferences.forEach(old_pref => {
    try {
      let new_pref = newPreferences.find(new_pref => new_pref.id === old_pref.id)
      // handle geo_permission
      if (old_pref.name === 'geo_permission') {

        if (new_pref && new_pref.responses[0].id === old_pref.responses[0].id) {
          new_pref.responses[0].selected = old_pref.responses[0].selected
          new_pref.responses[0].value = old_pref.responses[0].value
        }
      }

      // handle single select
      else if (old_pref.type === 'select' && old_pref.input === 'multiselect') {
        let old_response = old_pref.responses.find(old_res => old_res.selected)

        if (new_pref && old_response) {
          let new_response = new_pref.responses.find(new_res => new_res.id === old_response.id)
          if (new_response) {
            new_response.selected = old_response.selected
          }
        }
      }

      // handle text input
      else if (old_pref.input === 'textinput') {
        let value_ok = false
        let entered_value = old_pref.responses[0].value

        if (!new_pref || (new_pref.responses[0].id !== old_pref.responses[0].id) || !entered_value) {
          // dont pass if no matching new pref or panel response ids different or no entered value
        }
        else if (new_pref.options.inputValidation) {
          let new_reg = new RegExp(new_pref.options.inputValidation)
          if (new_reg.test(entered_value as string)) {
            value_ok = true
          }
        }
        else value_ok = true // value entered AND no input validation to test against

        if (value_ok) {
          new_pref.responses[0].selected = old_pref.responses[0].selected
          new_pref.responses[0].value = old_pref.responses[0].value
          new_pref.responses[0].unknown = old_pref.responses[0].unknown
        }
      }
    }
    catch (e) {}

  })

  return newPreferences;
};

export const usePreferenceStore = defineStore('preference', {
  persist: true,
  state: (): PreferenceState => ({
    preferences: null,
    hash: null,
    engagementKey: null,
    heartRateInputMethod: null,
    respiratoryRateInputMethod: null,
  }),

  getters: {
    // live: input widgets write `selected` straight onto these
    all: (state): PreferencePanel[] => state.preferences || [],

    get(): (name: string | undefined) => PreferencePanel {
      return (name) => this.all.find(p => p.name === name) || {};
    },

    // detached copy: callers must not write back into the panel config
    getResponse(): (name: string, response?: string) => PreferenceResponse {
      return (name, response) => {
        let resp: PreferenceResponse = {}
        try {
          const panel = this.get(name)
          resp = ['value', 'date'].includes(panel.type)
            ? panel.responses[0]
            : panel.responses.find(r => r.value === response) || {}
        }
        catch (e) {}
        return JSON.parse(JSON.stringify(resp))
      };
    },

    /*
    Used to find the order for the bottom bar for preferences.
    Language and Location preferences are handled via other means so this
    logic will return the index and total only for preference *panels* and 0 otherwise.

    Takes the name: reading the route here would import vuex and create a cycle.
    */
    getOrder(): (name: string | undefined) => PreferenceOrder {
      return (name) => {
        const ind = this.all.findIndex(pref => pref.name === name);

        return {
          total: this.all.length,
          ind: Math.max(0, ind),
        };
      };
    },

    // The first panel with no selected response, or {} when every panel is answered.
    nextIncomplete(): PreferencePanel {
      let panel: PreferencePanel | undefined
      try {
        this.all.every(preference => {
          if (preference.responses.some(response => response.selected)) {
            return true
          }
          panel = preference
          return false
        })
      }
      catch (e) {
        console.log(e)
      }
      return panel || {}
    },

    getHeartRateInputMethod(state): NumericInputMethod {
      return state.heartRateInputMethod || usePartnerConfigStore().heartRateInputMethod;
    },

    getRespiratoryRateInputMethod(state): NumericInputMethod {
      return state.respiratoryRateInputMethod || usePartnerConfigStore().respiratoryRateInputMethod;
    },
  },

  actions: {
    loadFromConfig(config: Config): void {
      // Pinia persists under one fixed key where vuex bucketed per engagement
      const key = readCookie('session');
      const sameEngagement = this.engagementKey === key;

      // hash is an md5 of panel content, so cloned engagements share it.
      // `all.length` guards against a hash without panels, which would never recover.
      if (config.hashes.preferences === this.hash && sameEngagement && this.all.length > 0) {
        return;
      }

      if (!sameEngagement) {
        // per-engagement before this store; partnerConfig supplies each default
        this.heartRateInputMethod = null;
        this.respiratoryRateInputMethod = null;
      }

      this.preferences = hydrateFromExisting(sameEngagement ? this.all : [], config.preferences);
      this.hash = config.hashes.preferences;
      this.engagementKey = key;
    },

    clearResponses(panel: PreferencePanel): void {
      panel.responses.forEach(r => {
        r.selected = false
      })
    },
  },
})
